/* eslint-disable react-refresh/only-export-components */
import {
  EditableButtons,
  EditableDataProvider,
  useEditableData,
} from "@/components/custom/editable-data";
import { useCurrentRef } from "@/hooks/use-current-ref";
import React from "react";
import { functionalUpdate } from "@tanstack/react-table";
import { cn, setForm } from "@/lib/utils";
import SelectInput, {
  OptionType,
} from "@/components/select-input/select-input";
import { FormDatePickerItem } from "@/components/custom/form-inputs/date-picker";

import {
  FormItemCommonProps,
  FormItemContent,
} from "@/components/custom/form-inputs/common";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { useTimeSettings } from "@/state/time-settings";
import _ from "lodash";
import { CreateScheduledTrainingDto } from "@/client";
import { FormDateRangePickerItem } from "@/components/custom/form-inputs/date-range-picker";
import { FormCheckbox } from "@/components/custom/form-inputs/checkbox";
import { useScheduledTrainingJob } from "../-common";
import {
  DayOfWeek,
  RECURRING_MODES,
  RecurrenceSchema,
  RecurringMode,
  WeekdayPicker,
  dayOfWeekIn,
  recurringModeOf,
  recurringModeValues,
  toStoredDays,
} from "../../-recurrence";
import { zodResolver } from "@hookform/resolvers/zod";

export type ScheduleData = Partial<
  Pick<
    CreateScheduledTrainingDto,
    | "isTrainingWindow"
    | "windowEnd"
    | "windowStart"
    | "recurring"
    | "recurringDays"
    | "scheduledFor"
  > & { runNow?: boolean; recurringMode?: RecurringMode }
>;

const TrainingScheduleStartExisting = [
  //   "recurring",
  "interval",
  "once",
  "draft",
] as const;

const TrainingScheduleStart = [
  ...TrainingScheduleStartExisting,
  "runNow",
] as const;

type TrainingScheduleStartExisting =
  (typeof TrainingScheduleStartExisting)[number];
type TrainingScheduleStart = (typeof TrainingScheduleStart)[number];

type TrainingScheduleStartState = {
  schedule: TrainingScheduleStart | undefined;
  setSchedule: React.Dispatch<
    React.SetStateAction<TrainingScheduleStart | undefined>
  >;
};

type EditableDataTrainingScheduleStartProps = {
  isNullable?: boolean;
};

function useEditableDataTrainingScheduleStartState({
  isNullable,
}: EditableDataTrainingScheduleStartProps) {
  const { data, form, editMode } = useEditableData<ScheduleData>();
  const dataRef = useCurrentRef(data);
  const runNow = form.watch("runNow");
  const schedule__ = (
    (data?.recurring ?? 0) > 0
      ? "interval"
      : runNow
        ? "runNow"
        : (data?.recurring ?? 0) <= -1
          ? "draft"
          : data?.scheduledFor
            ? "once"
            : undefined
  ) as TrainingScheduleStart | undefined;

  const [schedule_, setSchedule_] = React.useState(schedule__);
  React.useEffect(() => schedule__ && setSchedule_(schedule__), [schedule__]);
  const schedule = editMode ? schedule_ : schedule__;
  const setSchedule = React.useCallback<typeof setSchedule_>(
    (a) =>
      setSchedule_((s) => {
        const next = functionalUpdate(a, s);
        if (next !== s && editMode) {
          // the weekdays are set on their own: `setForm` would walk the array's own keys and write it
          // back index by index, which cannot shrink a selection
          const setDays = (days: ScheduleData["recurringDays"]) =>
            form.setValue("recurringDays", toStoredDays(days));
          if (next === "interval") {
            setForm(form, {
              recurring:
                (dataRef?.current?.recurring ?? -1) > 0
                  ? dataRef?.current?.recurring
                  : 1,
              recurringMode: undefined,
              runNow: false,
            });
            setDays(dataRef?.current?.recurringDays);
          } else if (next === "once") {
            setForm(form, {
              recurring: 0,
              recurringMode: undefined,
              scheduledFor:
                dataRef?.current?.scheduledFor ??
                new Date(+new Date() + 18000000),
              runNow: false,
            });
            setDays(null);
          } else if (next === "runNow") {
            setForm(form, {
              recurring: (isNullable ? null : undefined) as unknown as number,
              recurringMode: undefined,
              scheduledFor: (isNullable ? null : undefined) as unknown as Date,
              runNow: true,
            });
            setDays(null);
          } else if (next === "draft") {
            setForm(form, {
              recurring: -1,
              recurringMode: undefined,
              scheduledFor: (isNullable ? null : undefined) as unknown as Date,
              runNow: false,
            });
            setDays(null);
          }
        }
        return next;
      }),
    [form, editMode],
  );
  return { schedule, setSchedule };
}

export const TrainingScheduleStartContext =
  React.createContext<TrainingScheduleStartState>(
    undefined as unknown as TrainingScheduleStartState,
  );

export function TrainingScheduleStartProvider({
  defaultValue,
  children,
}: {
  defaultValue?: TrainingScheduleStart;
  children?: React.ReactNode;
}) {
  const [schedule, setSchedule] = React.useState<
    TrainingScheduleStart | undefined
  >(defaultValue);
  return (
    <TrainingScheduleStartContext.Provider
      value={{ schedule, setSchedule }}
      children={children}
    />
  );
}

export function EditableDataTrainingScheduleStartProvider({
  children,
  ...props
}: {
  children?: React.ReactNode;
} & EditableDataTrainingScheduleStartProps) {
  return (
    <TrainingScheduleStartContext.Provider
      value={useEditableDataTrainingScheduleStartState(props)}
      children={children}
    />
  );
}

export function useTrainingScheduleStart() {
  return React.useContext(TrainingScheduleStartContext);
}

// const asOption = (v: TrainingScheduleStart, t: TFunction) =>
//   ({
//     value: v.label,
//     label: t(v.label),
//     data: v,
//   }) satisfies OptionType<string> & { data: TrainingScheduleStart };

const asOption = (value: TrainingScheduleStart, t: TFunction) =>
  ({
    value,
    label: t(value),
  }) satisfies OptionType<TrainingScheduleStart>;

function TrainingScheduleStartToggle({
  omitRunNow,
  ...props
}: Omit<
  Parameters<typeof SelectInput>[0],
  "isMulti" | "isClearable" | "onChange" | "value" | "options"
> & { omitRunNow?: boolean }) {
  const { editMode } = useEditableData<ScheduleData>();
  const { schedule, setSchedule } = useTrainingScheduleStart();
  const { t } = useTranslation();
  return (
    editMode && (
      <SelectInput
        {...(props as {})}
        className={cn("w-full", props.className)}
        isMulti={false}
        isClearable={true}
        options={(omitRunNow
          ? TrainingScheduleStartExisting
          : TrainingScheduleStart
        ).map((v) => asOption(v, t))}
        value={schedule ? asOption(schedule, t) : undefined}
        onChange={(op) => setSchedule(op!.value)}
      />
    )
  );
}

/**
 * Every Day / Every Week / Custom Days. The first two are presets over the same representation the
 * backend stores (`recurring` plus an empty `recurringDays`); Custom Days is the day-of-week list.
 */
function Recurring({ className }: { className?: string }) {
  const { t } = useTranslation();
  const { form, editMode } = useEditableData<ScheduleData>();
  const { timeZone } = useTimeSettings();
  const recurring = form.watch("recurring");
  const recurringDays = form.watch("recurringDays");
  const recurringMode = form.watch("recurringMode");
  const scheduledFor = form.watch("scheduledFor");
  const mode = recurringMode ?? recurringModeOf({ recurring, recurringDays });
  const daysError = form.formState.errors.recurringDays?.message as
    | string
    | undefined;
  const asModeOption = React.useCallback(
    (value: RecurringMode) => ({ value, label: t(value) }),
    [t],
  );
  /**
   * `setForm` walks `Object.entries`, and an array is an object - it would write the days back
   * index by index and leave a stale tail behind whenever the selection shrinks. Arrays have to be
   * replaced wholesale, and an empty selection is stored as null rather than [].
   */
  const setDays = React.useCallback(
    (days: DayOfWeek[] | null) =>
      form.setValue("recurringDays", toStoredDays(days), {
        shouldDirty: true,
      }),
    [form],
  );
  /**
   * The picker's own writes also pin the mode. An existing Custom Days schedule arrives with
   * `recurringMode` unset - it is derived from the stored days - so unticking the last chip would
   * otherwise re-derive as Every Week, hide the picker and submit a *different* schedule instead of
   * failing the "select at least one day" rule.
   */
  const setCustomDays = React.useCallback(
    (days: DayOfWeek[]) => {
      form.setValue("recurringMode", "customDays", { shouldDirty: true });
      setDays(days);
    },
    [form, setDays],
  );

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <FormItemContent label={t("recurring")} clear={null}>
        <SelectInput
          className="w-full"
          isMulti={false}
          isClearable={false}
          isDisabled={!editMode}
          options={RECURRING_MODES.map(asModeOption)}
          value={asModeOption(mode)}
          onChange={(op) => {
            const next = recurringModeValues(
              op!.value as RecurringMode,
              { recurring, recurringDays },
              // a fresh Custom Days selection starts on the start date's own weekday
              dayOfWeekIn(
                scheduledFor ? new Date(scheduledFor) : new Date(),
                timeZone,
              ),
            );
            setForm(form, _.omit(next, "recurringDays"));
            setDays(next.recurringDays);
          }}
        />
      </FormItemContent>
      {mode === "customDays" && (
        <div className="flex flex-col gap-1">
          <WeekdayPicker
            disabled={!editMode}
            value={recurringDays}
            onChange={setCustomDays}
          />
          {daysError && (
            <span className="text-xs text-destructive">{daysError}</span>
          )}
        </div>
      )}
    </div>
  );
}

function IsWindow(
  props: Omit<
    Parameters<typeof FormCheckbox>[0],
    "form" | "name" | "label" | "disabled"
  >,
) {
  const { form, editMode } = useEditableData<ScheduleData>();
  const { t } = useTranslation();
  return (
    <FormCheckbox
      form={form}
      name={"isTrainingWindow"}
      label={t("isTrainingWindow")}
      disabled={!editMode}
      {...(props as {})}
    />
  );
}

function Window(
  props: Omit<
    Parameters<typeof FormDateRangePickerItem>[0],
    "form" | "from" | "to" | "label" | "isDisabled"
  >,
) {
  const { form, editMode } = useEditableData<ScheduleData>();
  const { t } = useTranslation();
  const timeSettings = useTimeSettings();
  const isTrainingWindow = form.watch("isTrainingWindow");
  return (
    isTrainingWindow && (
      <FormDateRangePickerItem
        form={form}
        from="windowStart"
        to="windowEnd"
        label={t("trainingWindow")}
        isDisabled={!editMode}
        {...timeSettings}
        {...(props as {})}
      />
    )
  );
}

function Schedule({
  label: _label,
  container,
  className,
}: FormItemCommonProps & { className?: string }) {
  const { editMode } = useEditableData<ScheduleData>();
  const { schedule } = useTrainingScheduleStart();
  const { t } = useTranslation();
  return (
    <FormItemContent
      {...container}
      label={editMode ? undefined : t(schedule ?? "schedule")}
      clear={null}
    >
      <div className={cn("flex flex-col gap-2", className)}>
        {schedule === "interval" ? (
          <>
            <Recurring />
            <IsWindow />
            <Window />
          </>
        ) : // ) : schedule === "interval" ? (
        //   !editMode && <span>{"interval"}</span>
        schedule === "runNow" ? (
          !editMode && <span>{t("runNow")}</span>
        ) : undefined}
      </div>
    </FormItemContent>
  );
}

function ScheduledFor(
  props: Omit<
    Parameters<typeof FormDatePickerItem>[0],
    "form" | "name" | "isDisabled" | "label"
  >,
) {
  const { form, editMode } = useEditableData<ScheduleData>();
  const { t } = useTranslation();
  const timeSettings = useTimeSettings();
  //   const timezone = form.watch("timezone");
  //   const timeSettings = React.useMemo(() => as(timezone), [as, timezone]);
  return (
    <FormDatePickerItem
      inline={true}
      {...props}
      container={{
        ...props.container,
        clear: props.container?.clear ?? (editMode ? undefined : null),
        clearProps: {
          resetTarget: null,
          isResettable: (v) => v !== null && v !== undefined,
        },
        inline: false,
      }}
      form={form}
      name="scheduledFor"
      label={t("startDate")}
      placeholder={editMode ? "Pick a Date" : "<N/A>"}
      isDisabled={!editMode}
      {...timeSettings}
    />
  );
}

export function TrainingScheduledJobsScheduleOnce() {
  return (
    <div className="flex flex-col items-start gap-2">
      <ScheduledFor className="min-w-64 w-full" />
    </div>
  );
}

export function TrainingScheduledJobsScheduleContent() {
  const { schedule } = useTrainingScheduleStart();

  return (
    <div className="flex flex-col items-start gap-2">
      <Schedule className="min-w-64 w-full" />
      {schedule !== "runNow" && schedule !== "draft" && (
        <>
          <ScheduledFor className="min-w-64 w-full" />
        </>
      )}
    </div>
  );
}

export function TrainingScheduledJobsSchedule() {
  const {
    scheduledJobId,
    scheduledJobResult: { data: scheduledJob },
    edit,
  } = useScheduledTrainingJob();

  return (
    <EditableDataProvider<ScheduleData>
      data={React.useMemo(
        () =>
          _.pick(scheduledJob ?? {}, [
            "name",
            "isTrainingWindow",
            "windowEnd",
            "windowStart",
            "recurring",
            "recurringDays",
            "scheduledFor",
          ]),
        [scheduledJob],
      )}
      editable={true}
      id={scheduledJobId}
      isDefault={true}
      loading={edit.isPending}
      resolver={zodResolver(RecurrenceSchema)}
      onSubmit={(data) =>
        // `runNow` and `recurringMode` are UI-only state, not part of the DTO
        edit.mutateAsync({
          requestBody: _.omit(data, ["runNow", "recurringMode"]),
        })
      }
    >
      <EditableDataTrainingScheduleStartProvider isNullable={true}>
        <div className="-ml-2 pl-2 w-full relative">
          <div className="absolute top-0 left-0 w-full flex items-center gap-2">
            <div className="ml-auto flex items-center gap-2">
              <EditableButtons />
            </div>
          </div>
          <TrainingScheduleStartToggle
            omitRunNow={true}
            className="pb-2 w-[calc(100%-7.5rem)]"
          />
          <TrainingScheduledJobsScheduleContent />
        </div>
      </EditableDataTrainingScheduleStartProvider>
    </EditableDataProvider>
  );
}
